import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { POST } from '../../../configs/api';
import { api, states } from '../../../utils/constants';
import { getActiveAdmins } from '../../../store/slices/usersSlice';
import { useSelector, useDispatch } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { addClient } from '../../../store/slices/clientsSlice';
import { canManageClientsDirectly } from '../../../utils/permissions';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';
import { addClientColor } from '../../../utils/clientColors';
import ClientInvoicePatternField from './ClientInvoicePatternField';
import { ADD_CLIENT_FIELD_ORDER, useClientFormValidation } from '../../../utils/clientFormValidation';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

const emptyClient = {client_name: '',phone: '',city: '',state: '',zip_code: '',display_as: '',client_address: '',address_2: '',email: '',invoice_pattern: '',admins: [],request_notes: '',latitude: null,longitude: null};

export default function AddClientDialog({ setAddClientDialogOpen, addClientDialogOpen, user }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const admins = useSelector(getActiveAdmins);
  const [showError, setShowError] = useState('');
  const [newClient, setNewClient] = useState(emptyClient);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const canManageDirectly = canManageClientsDirectly(user);
  const newClientRef = useRef(newClient);
  newClientRef.current = newClient;
  const displayAsLinkedRef = useRef(true);
  const {
    errors,
    formRef,
    handleFieldChange,
    handleFieldBlur,
    revalidateFields,
    validateAll,
    resetValidation,
    applyDuplicateError,
  } = useClientFormValidation({
    includeRequestNotes: !canManageDirectly,
    fieldOrder: ADD_CLIENT_FIELD_ORDER,
  });

  useEffect(() => {
    if (addClientDialogOpen) {
      resetValidation();
      setShowError('');
      const current = newClientRef.current;
      displayAsLinkedRef.current = (current.display_as || '') === (current.client_name || '');
    }
  }, [addClientDialogOpen, resetValidation]);

    const handleAddClient = async () => {
      setShowError('');
      if (!validateAll(newClientRef.current)) return;

      setLoading(true);
      const { status, data, message } = await POST(api.createClient, {}, { ...newClient, admins: newClient.admins.map(admin => admin.id) });
      if (status === 201) {
        if (data?.id != null) addClientColor(user?.company_id, data.id);
        setShowSuccessToast(true);
        setNewClient(emptyClient);
        resetValidation();
        displayAsLinkedRef.current = true;
        canManageDirectly && dispatch(addClient({...data, admins: newClient.admins}));
      } else if (status === 409) {
        setShowError(applyDuplicateError(data?.message || message));
      } else {
        setShowError(data?.message || message || 'Something went wrong, please try again later.');
      }
      setLoading(false);
    };

    const updateClientField = (field, value) => {
      if (field === 'display_as') {
        displayAsLinkedRef.current = value === (newClientRef.current.client_name || '');
      }
      const shouldSyncDisplayAs = field === 'client_name' && displayAsLinkedRef.current;

      setNewClient((prev) => {
        const next = { ...prev, [field]: value };
        if (shouldSyncDisplayAs) {
          next.display_as = value;
        }
        newClientRef.current = next;
        return next;
      });
      handleFieldChange(field, value);
      if (shouldSyncDisplayAs) {
        handleFieldChange('display_as', value);
      }
    };

    const handlePlaceSelect = (addressData) => {
      const formattedZip = (addressData.zipCode && addressData.zipCode.trim())
        ? formatZipCode(addressData.zipCode)
        : '';
      const prev = newClientRef.current;
      const next = {
        ...prev,
        client_address: addressData.address || prev.client_address,
        city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
        state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
        zip_code: formattedZip,
        latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
        longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
      };
      newClientRef.current = next;
      setNewClient(next);
      revalidateFields(next, ['client_address', 'city', 'state', 'zip_code']);
    };
  
  return (
    <Dialog
      open={addClientDialogOpen}
      onClose={() => setAddClientDialogOpen(false)}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '40vw',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : '90vh',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move'
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          {canManageDirectly ? 'Add Client' : 'Request to add client'}
        </Typography>
        <IconButton 
          onClick={() => setAddClientDialogOpen(false)} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {showError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setShowError('')}>
            {showError}
          </Alert>
        )}

        <Box ref={formRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            name="client_name"
            label="Client Name"
            value={newClient.client_name || ''}
            onChange={(event) => updateClientField('client_name', event.target.value)}
            onBlur={() => {
              handleFieldBlur('client_name', newClientRef.current.client_name);
              if (displayAsLinkedRef.current) {
                handleFieldBlur('display_as', newClientRef.current.display_as);
              }
            }}
            error={!!errors.client_name}
            helperText={errors.client_name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <TextField
            fullWidth
            required
            name="display_as"
            label="Display As"
            value={newClient.display_as || ''}
            onChange={(event) => updateClientField('display_as', event.target.value)}
            onBlur={() => handleFieldBlur('display_as', newClientRef.current.display_as)}
            error={!!errors.display_as}
            helperText={errors.display_as || 'Follows client name until you edit it'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <ClientInvoicePatternField
            required
            value={newClient.invoice_pattern}
            onChange={(invoice_pattern) => updateClientField('invoice_pattern', invoice_pattern)}
            onBlur={() => handleFieldBlur('invoice_pattern', newClientRef.current.invoice_pattern)}
            error={errors.invoice_pattern}
          />

          <TextField
            fullWidth
            name="email"
            label="Email"
            type="email"
            value={newClient.email || ''}
            onChange={(event) => updateClientField('email', event.target.value)}
            onBlur={() => handleFieldBlur('email', newClientRef.current.email)}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              }
            }}
          />

          {/* Admins */}
          {canManageDirectly && (
            <Autocomplete 
              multiple 
              id="admins-checkboxes" 
              options={admins || []} 
              getOptionLabel={(option) => option.email} 
              value={newClient.admins} 
              onChange={(_, value) => updateClientField('admins', value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiAutocomplete-listbox': {
                  borderRadius: 1,
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  backgroundColor: 'background.paper',
                  padding: 0,
                }
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '8px 16px',
                      margin: 0,
                      backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                    }}
                  >
                    <Checkbox 
                      icon={icon} 
                      checkedIcon={checkedIcon} 
                      checked={selected}
                      sx={{ 
                        marginRight: 2,
                        padding: '4px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem',
                        }
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 400,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {option.email}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Admins" 
                  placeholder="Admins" 
                  fullWidth 
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#fff' : '#000',
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }
                  }}
                />
              )}
            />
          )}

          <TextField
            fullWidth
            required
            name="phone"
            label="Phone"
            value={newClient.phone || ''}
            onChange={(event) => updateClientField('phone', formatPhoneNumber(event.target.value))}
            onBlur={() => handleFieldBlur('phone', newClientRef.current.phone)}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                      +1
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="(555) 123-4567"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <GooglePlacesAutocomplete
            required
            name="client_address"
            label="Client Address"
            value={newClient.client_address || ''}
            onChange={(value) => updateClientField('client_address', value)}
            onBlur={() => handleFieldBlur('client_address', newClientRef.current.client_address)}
            onPlaceSelect={handlePlaceSelect}
            error={!!errors.client_address}
            helperText={errors.client_address}
            placeholder=""
          />

          <TextField
            fullWidth
            name="address_2"
            label="Address Line 2"
            value={newClient.address_2 || ''}
            onChange={(e) => updateClientField('address_2', e.target.value)}
            onBlur={() => handleFieldBlur('address_2', newClientRef.current.address_2)}
            error={!!errors.address_2}
            helperText={errors.address_2}
            placeholder="Suite, unit, floor, etc."
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <OptimizedCityAutocomplete
            value={newClient.city || ''}
            onChange={(value) => updateClientField('city', value || '')}
            renderInput={(params) => (
              <TextField 
                {...params} 
                required
                name="city"
                label="City" 
                placeholder="Select or type city name" 
                fullWidth 
                error={!!errors.city}
                helperText={errors.city}
                onBlur={(event) => {
                  const typed = event.target.value || '';
                  if (typed !== (newClientRef.current.city || '')) {
                    updateClientField('city', typed);
                  }
                  handleFieldBlur('city', typed);
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#000',
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    color: 'text.primary',
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem',
                  }
                }}
              />
            )}
          />

          <Autocomplete
            options={Object.keys(states)}
            value={newClient.state || ''}
            onChange={(event, value) => updateClientField('state', value || '')}
            getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: isDarkMode ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: '"Space Grotesk", Inter, sans-serif',
                '&:hover': {
                  boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: isDarkMode ? 'linear-gradient(135deg, #334155 0%, #475569 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-focused': {
                  boxShadow: isDarkMode ? '0 8px 30px rgba(0, 0, 0, 0.4)' : '0 8px 30px rgba(0, 0, 0, 0.12)',
                  background: isDarkMode ? 'linear-gradient(135deg, #334155 0%, #475569 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
                  transform: 'translateY(-2px)',
                }
              },
              '& .MuiAutocomplete-listbox': {
                borderRadius: 2,
                boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                border: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.08)',
                background: isDarkMode ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                padding: 0,
                overflow: 'hidden',
                fontFamily: '"Space Grotesk", Inter, sans-serif',
              }
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                      padding: '8px 16px',
                      margin: 0,
                      backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                    }}
                  >
                    <Typography 
                      variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 400,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    {states[option]} - {option}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                required
                name="state"
                label="State" 
                placeholder="Select state" 
                fullWidth 
                error={!!errors.state}
                helperText={errors.state}
                onBlur={() => handleFieldBlur('state', newClientRef.current.state)}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                    '&.Mui-focused': {
                      color: isDarkMode ? '#fff' : '#1976d2',
                      fontWeight: 600
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                    color: 'text.primary',
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                  }
                }}
              />
            )}
          />

          <TextField
            fullWidth
            required
            name="zip_code"
            label="Zip Code"
            value={newClient.zip_code || ''}
            onChange={(event) => updateClientField('zip_code', formatZipCode(event.target.value))}
            onBlur={() => handleFieldBlur('zip_code', newClientRef.current.zip_code)}
            error={!!errors.zip_code}
            helperText={errors.zip_code}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="12345 or 12345-6789"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Request Notes */}
          {!canManageDirectly && (
            <TextField
              fullWidth
              name="request_notes"
              label="Request Notes"
              value={newClient.request_notes || ''}
              onChange={(event) => updateClientField('request_notes', event.target.value)}
              onBlur={() => handleFieldBlur('request_notes', newClientRef.current.request_notes)}
              error={!!errors.request_notes}
              helperText={errors.request_notes}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: isDarkMode ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: '"Space Grotesk", Inter, sans-serif',
                '&:hover': {
                  boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: isDarkMode ? 'linear-gradient(135deg, #334155 0%, #475569 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-focused': {
                  boxShadow: isDarkMode ? '0 8px 30px rgba(0, 0, 0, 0.4)' : '0 8px 30px rgba(0, 0, 0, 0.12)',
                  background: isDarkMode ? 'linear-gradient(135deg, #334155 0%, #475569 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
                  transform: 'translateY(-2px)',
                }
              },
              '& .MuiInputLabel-root': {
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.95rem',
                fontFamily: '"Space Grotesk", Inter, sans-serif',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#1976d2',
                  fontWeight: 600
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Space Grotesk", Inter, sans-serif',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontFamily: '"Space Grotesk", Inter, sans-serif',
              }
            }}
            />
          )}
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper',
      }}>
        <Button 
          onClick={() => setAddClientDialogOpen(false)}
          variant="outlined"
          sx={{ 
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': {
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
              backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
            }
          }}
        >
          Cancel
        </Button>
        
        <Button 
          variant="contained"
          disabled={loading}
          onClick={handleAddClient}
          sx={{ 
            backgroundColor: isDarkMode ? '#fff' : '#000',
            color: isDarkMode ? '#000' : 'white',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#475569' : '#666',
              color: isDarkMode ? '#94a3b8' : '#fff',
            }
          }}
        >
          {canManageDirectly ? 'Add Client' : 'Request to add client'}
        </Button>
      </Box>
      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {canManageDirectly ? 'Client added' : 'Requested to add client'}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}